import type { ArtistNestedType } from "@/pages/artists/schemas/artist.nested.form";
import type { VenueNestedType } from "@/pages/venues/schemas/venue.nested.form";

import type { EntityRecommendations } from "../hooks/useSandboxPersonas";
import type { Interaction } from "../types/interactions";

const DECAY_LABELS: Record<string, string> = {
  q1: "recent",
  q2: "mid-term",
  q3: "long-term",
};

const INTERACTION_VERBS: Record<string, string> = {
  view: "Visited profile page page of ",
  follow: "Followed",
  share: "Shared",
  screen_captured: "Screen-captured",
  ticket_view: "Viewed ticket for",
  ticket_purchase: "Purchased ticket for",
  offer_view: "Viewed offer for",
};

export function buildDescribePrompt(interactions: Interaction[]): string {
  const bullets = interactions
    .map((i) => {
      const verb = INTERACTION_VERBS[i.interactionType] ?? i.interactionType;
      const decay = i.decayPeriod
        ? ` (${DECAY_LABELS[i.decayPeriod] ?? i.decayPeriod})`
        : "";
      return `- ${verb} ${i.entityType} ${decay}: ${i.entity.details ?? ""}`;
    })
    .join("\n");

  return `You are analyzing a music recommendation system persona. Based on the following user interactions, write a concise 2–3 sentence description of this person's music taste profile.

Interactions:
${bullets}

Describe the persona's taste profile:`;
}

export function buildComparePrompt(
  description: string,
  recommendations: EntityRecommendations,
): string {
  const formatArtists = (items: EntityRecommendations["artists"], limit = 5) =>
    items
      .slice(0, limit)
      .map((r) => {
        const details: string[] = [];
        if (r.genres?.length) details.push(`genres: ${r.genres.join(", ")}`);
        const suffix = details.length ? ` (${details.join("; ")})` : "";
        return `${r.name} (${(r.similarity * 100).toFixed(1)}%)${suffix}`;
      })
      .join("\n  ");

  const formatGenres = (items: EntityRecommendations["genres"], limit = 5) =>
    items
      .slice(0, limit)
      .map((r) => {
        const details: string[] = [];
        if (r.parents?.length) details.push(`parents: ${r.parents.join(", ")}`);
        const suffix = details.length ? ` (${details.join("; ")})` : "";
        return `${r.name} (${(r.similarity * 100).toFixed(1)}%)${suffix}`;
      })
      .join("\n  ");

  const formatVenues = (items: EntityRecommendations["venues"], limit = 5) =>
    items
      .slice(0, limit)
      .map((r) => {
        const details: string[] = [];
        if (r.type) details.push(`type: ${r.type}`);
        if (r.address) details.push(`address: ${r.address}`);
        const suffix = details.length ? ` (${details.join("; ")})` : "";
        return `${r.name} (${(r.similarity * 100).toFixed(1)}%)${suffix}`;
      })
      .join("\n  ");

  const formatEvents = (items: EntityRecommendations["events"], limit = 5) =>
    items
      .slice(0, limit)
      .map((r) => {
        const details: string[] = [];
        if (r.type) details.push(`type: ${r.type}`);
        if (r.venueName) details.push(`venue: ${r.venueName}`);
        if (r.genres?.length) details.push(`genres: ${r.genres.join(", ")}`);
        if (r.lineup?.length)
          details.push(`lineup: ${r.lineup.slice(0, 5).join(", ")}`);
        const suffix = details.length ? ` (${details.join("; ")})` : "";
        return `${r.name} (${(r.similarity * 100).toFixed(1)}%)${suffix}`;
      })
      .join("\n  ");

  const sections = [
    `Artists:\n  ${formatArtists(recommendations.artists)}`,
    `Genres:\n  ${formatGenres(recommendations.genres)}`,
    `Venues:\n  ${formatVenues(recommendations.venues)}`,
    `Events:\n  ${formatEvents(recommendations.events)}`,
  ].join("\n\n");

  return `You are evaluating a music recommendation system. Compare the following persona description with the system's top recommendations.

Persona description:
"${description}"

Top recommendations:
${sections}

Do these recommendations align with the persona? Provide a brief analysis (2–3 sentences):`;
}
